import { format } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';
import { renderReminderTemplate } from '../../supabase/functions/_shared/reminderTemplate';

export interface TemplateVariables {
  customer_name: string;
  vehicle: string;
  shop_name: string;
  appointment_time: string;
  services: string;
  appointment_type: string;
}

/**
 * Render a template string with variables.
 * Core-100 R4 wave 3 (CORE-AZ Prov): the preview renders with the SAME function send-reminder sends with
 * (`supabase/functions/_shared/reminderTemplate.ts`: one pass, values inserted literally, one line each), so what the
 * manager previews is what the customer receives. The per-variable `replace(regex, value)` it replaced interpreted
 * `$'` / `$&` in a value and re-expanded `{placeholders}` typed into a record.
 */
export function renderTemplate(template: string, variables: TemplateVariables): string {
  return renderReminderTemplate(template, variables);
}

/**
 * Get available template variables with descriptions
 */
export function getTemplateVariables(): Array<{ key: string; description: string; example: string }> {
  return [
    { key: '{customer_name}', description: 'Customer first name', example: 'John' },
    { key: '{vehicle}', description: 'Vehicle year, make, and model', example: '2020 Toyota Camry' },
    { key: '{shop_name}', description: 'Your shop name', example: 'Quick Fix Auto' },
    { key: '{appointment_time}', description: 'Appointment date and time', example: 'Jan 15, 2:00 PM' },
    { key: '{services}', description: 'Services being performed', example: 'Oil change + Tire rotation' },
    { key: '{appointment_type}', description: 'Type of appointment', example: 'Drop-off' },
  ];
}

/**
 * Validate template string (check for unclosed braces, invalid variables)
 */
export function validateTemplate(template: string): { valid: boolean; errors: string[] } {
  const errors: string[] = [];
  
  // Check for unclosed braces
  const openBraces = (template.match(/\{/g) || []).length;
  const closeBraces = (template.match(/\}/g) || []).length;
  
  if (openBraces !== closeBraces) {
    errors.push('Template has unclosed braces');
  }
  
  // Check for invalid variables
  const validKeys = ['customer_name', 'vehicle', 'shop_name', 'appointment_time', 'services', 'appointment_type'];
  const variablePattern = /\{([^}]+)\}/g;
  let match;
  
  while ((match = variablePattern.exec(template)) !== null) {
    const varName = match[1];
    if (!validKeys.includes(varName)) {
      errors.push(`Unknown variable: {${varName}}`);
    }
  }
  
  return {
    valid: errors.length === 0,
    errors,
  };
}

/**
 * Generate sample variables for preview
 */
export function getSampleVariables(): TemplateVariables {
  return {
    customer_name: 'John',
    vehicle: '2020 Toyota Camry',
    shop_name: 'Quick Fix Auto',
    appointment_time: 'Jan 15, 2:00 PM',
    services: 'Oil change + Tire rotation',
    appointment_type: 'Drop-off',
  };
}
